import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { fingerscan } from "careplix-scan-sdk";
import { useAuth } from "../utils/auth";
import { sdkURL, api_key } from "../utils/api-url-list";

import LoadingScreen from "../components/LoadingScreen";

import Failure from "../assets/images/failure.gif";
import Analyzing from "../assets/images/analyzing.gif";
import {
  buildFingerScanConfig,
  normalizeScanDuration,
} from "../utils/scanDuration";

const FingerScan = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const {
    userData: { employee_id },
    agentInfo,
  } = useAuth();

  const videoInputRef = useRef();
  const canvasOutputRef = useRef();
  const startPromiseRef = useRef(Promise.resolve());
  const scanDuration = normalizeScanDuration(state?.scanDuration);

  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "",
    message: "",
    progress: 0,
    timeElapsed: 0,
  });

  useEffect(() => {
    const controller = new AbortController();
    const landscape = window.matchMedia("(orientation: landscape)");
    let cancelled = false;
    if (state?.scanToken?.length > 0) {
      setLoading(true);
      let duration = 0;
      fingerscan.onFrame((fd) => {
        if (cancelled) return;
        setScanFrameData(fd);
        duration = Math.round(fd.timeElapsed / 1000);
      });
      fingerscan.onScanFinish(async ({ raw_intensity, ppg_time, average_fps }) => {
        if (cancelled) return;
        try {
          controller.abort();
          setAnalyzing(true);
          const resp = await fetch(sdkURL("/vitals/add-scan"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              api_key,
              employee_id,
              scan_token: state?.scanToken,
              posture: state?.posture,
              dob: state?.userData?.dob,
              gender: state?.userData?.gender,
              metadata: {
                physiological_scores: { height: state?.userData?.height, weight: state?.userData?.weight },
                ppg_time: ppg_time,
                raw_intensity: raw_intensity,
                device: navigator.platform.match(/iPhone|iPod|iPad/)
                  ? "RPPG_CAREPLIX_FINGER_IOS"
                  : "RPPG_CAREPLIX_FINGER_ANDROID",
                fps: average_fps,
              },
              notes: { "Client name": "INDUSIND", relation: state?.userData?.relation, person_name: state?.userData?.person_name || "", referral_code: agentInfo?.code || "", phone_number: state?.userData?.phone_number || "", scan_duration_seconds: scanDuration, ...(state?.questionnaire || {}) },
            }),
          });
          const resp_json = await resp.json();
          if (resp_json.statusCode?.toString().startsWith("2")) {
            if (state?.userData?.phone_number && agentInfo?.code) {
              try {
                await fetch("/api/record-scan", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    phone_number: state.userData.phone_number,
                    referral_code: agentInfo.code,
                    relation: state?.userData?.relation || "Self",
                    person_name: state?.userData?.person_name || "",
                    gender: state?.userData?.gender || "",
                    scan_id: resp_json.scan_id || "",
                  }),
                });
              } catch (recordErr) {
                console.error("Failed to record scan:", recordErr);
              }
            }
            if (state?.questionnaireId && resp_json.scan_id) {
              try {
                await fetch(`/api/health-questionnaire/${state.questionnaireId}`, {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ scan_id: resp_json.scan_id }),
                });
              } catch (qErr) {
                console.error("Failed to update questionnaire scan_id:", qErr);
              }
            }
            navigate("/scan-result", { state: { result: resp_json, scanDuration }, replace: true });
          } else throw new Error(resp_json.message);
        } catch (err) {
          console.error(err);
          setError(err.message);
        } finally {
          fetch(sdkURL("/vitals/update-scan-status"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              api_key,
              scan_token: state?.scanToken,
              employee_id,
              fire_type: "on_success",
              fire_reason: "fingerScan.onScanFinish",
              error_list: [],
            }),
          })
            .then((resp) => resp.json())
            .then((json) => {
              if (!json.statusCode?.toString().startsWith("2")) throw new Error(json.message ?? "Unable to send Error logs.");
            })
            .catch(console.error);
        }
      });
      fingerscan.onError((err, code) => {
        if (cancelled) return;
        console.error(err);
        setError(err.message);
        fetch(sdkURL("/vitals/update-scan-status"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            api_key,
            scan_token: state?.scanToken,
            employee_id,
            fire_type: "on_error",
            fire_reason: "fingerScan.onError",
            error_list: [{ code, duration }],
          }),
        })
          .then((resp) => resp.json())
          .then((json) => {
            if (!json.statusCode?.toString().startsWith("2")) throw new Error(json.message ?? "Unable to send Error logs.");
          })
          .catch(console.error);
      });
      const startPromise = startPromiseRef.current
        .catch(() => {})
        .then(() => {
          if (cancelled) return false;
          return fingerscan
            .startScan(
              buildFingerScanConfig(scanDuration, {
                videoElement: videoInputRef.current,
                canvasElement: canvasOutputRef.current,
              })
            )
            .then(() => true);
        });
      startPromiseRef.current = startPromise;
      startPromise
        .then((started) => {
          if (!started) return;
          if (cancelled) {
            fingerscan.stopScan();
            return;
          }
          console.log("Scan Started");
          landscape.addEventListener(
            "change",
            (e) => {
              if (e.matches) {
                fingerscan.stopScan();
                const err = new Error("Screen orientation changed to Landscape");
                console.error(err);
                setError(err.message);
              }
            },
            { signal: controller.signal },
          );
          duration = 0;
        })
        .catch((err) => {
          if (cancelled) return;
          console.error(err);
          setError(err?.cause?.message || err?.message || "Unable to start finger scan.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else setError("Invalid Scan Token");
    return () => {
      cancelled = true;
      controller.abort();
      fingerscan.stopScan();
    };
  }, [navigate, employee_id, agentInfo, state, scanDuration]);

  const scanMessages = useMemo(
    () =>
      [
        "We burn calories while we are asleep because brain activity requires energy",
        "Exercise promotes cardiovascular health, ensuring more blood and oxygen to circulate the body, helping to elevate energy",
        "Always sleep on your back straight since it allows your neck and spine in a neutral position",
        "Alcohol consumption disrupts chemical impulses between brain cells causing impulsive behaviour, slurred speech, impaired memory",
        "Meditation increases nitric oxide generation in the brain helping to lower your blood pressure",
        "Meditation reduces the production of cytokines in several recent studies, thus helping in stress reduction",
        "Laughing is good for your heart. It reduces stress and gives a boost to your immune system",
        "Lack of sleep can lead to heart disease, heart attack, or stroke",
        "Heart pumps about 2,000 gallons of blood every day",
        "Your heart will beat about 115,000 times each day",
      ].sort(() => Math.random() - 0.5),
    [],
  );

  const currentMessage = useMemo(() => {
    if (scanFrameData.type === "error") return "---";
    const timeElapsed = scanFrameData.timeElapsed / 1000;
    if (timeElapsed <= 5) return "Cover your back camera with your finger";
    else if (timeElapsed <= 10) return "During the measurement, please do not speak or move";
    else if (timeElapsed <= 15) return "Keep your device steady throughout the vital measurement";
    else if (timeElapsed <= 20) return scanMessages[0];
    else if (timeElapsed <= 25) return scanMessages[1];
    else if (timeElapsed <= 30) return scanMessages[2];
    else if (timeElapsed <= 35) return scanMessages[3];
    else if (timeElapsed <= 40) return scanMessages[4];
    else if (timeElapsed <= 45) return scanMessages[5];
    else if (timeElapsed <= 50) return scanMessages[6];
    else if (timeElapsed <= 55) return scanMessages[7];
    else if (timeElapsed <= 60) return scanMessages[8];
    else if (timeElapsed <= 65) return scanMessages[9];
  }, [scanFrameData.timeElapsed, scanFrameData.type, scanMessages]);

  const progressPercent = Math.min(100, Math.round((scanFrameData.timeElapsed / (scanDuration * 1000)) * 100));

  return (
    <div className="relative h-screen w-screen bg-black">
      <video ref={videoInputRef} id="videoInput" className="fixed bottom-12 left-8 h-px w-px" autoPlay muted playsInline />
      <canvas ref={canvasOutputRef} id="canvasOutput" className="w-full h-full -scale-x-100" />

      <div className="fixed bottom-0 left-0 right-0">
        <div className="mx-4 mb-4 rounded-2xl overflow-hidden bg-white/90 backdrop-blur-lg shadow-xl border border-white/20">
          <div className="h-1 bg-gray-100">
            <div
              className="h-full bg-gradient-to-r from-[#1845a3] to-[#ED1C24] transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="p-4">
            <div className="mb-2">
              <h4 className="text-sm font-bold text-gray-800">
                {scanFrameData.type === "error" ? "Scan Stopped" : scanFrameData.message || "Initializing..."}
              </h4>
            </div>
            <p className="text-xxs text-gray-500 leading-relaxed">{currentMessage}</p>
            <button
              type="button"
              onClick={() => {
                fingerscan.stopScan();
                fetch(sdkURL("/vitals/update-scan-status"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    api_key,
                    scan_token: state?.scanToken,
                    employee_id,
                    fire_type: "on_halt",
                    fire_reason: "cancel-scan",
                    error_list: [],
                  }),
                })
                  .then((resp) => resp.json())
                  .then((json) => {
                    if (!json.statusCode?.toString().startsWith("2")) throw new Error(json.message ?? "Unable to send Error logs.");
                  })
                  .catch(console.error);
                navigate(-1);
              }}
              className="mt-3 text-xs font-semibold text-red-500"
            >
              Cancel Scan
            </button>
          </div>
        </div>
      </div>

      {scanFrameData.type === "error" && (
        <div className="fixed top-6 left-4 right-4">
          <p className="px-4 py-2 rounded-xl bg-red-500/90 backdrop-blur text-white text-center text-xs font-medium shadow-lg">
            {scanFrameData.message}
          </p>
        </div>
      )}

      {analyzing && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-white">
          <div className="relative">
            <div className="absolute -inset-4 rounded-full bg-gradient-to-r from-[#1845a3]/20 to-[#ED1C24]/20 animate-pulse" />
            <img className="relative shrink-0 w-20" src={Analyzing} alt="analyzing" />
          </div>
          <p className="mt-6 bg-gradient-to-r from-[#1845a3] to-[#ED1C24] bg-clip-text text-transparent text-xl font-bold">Analyzing Data</p>
          <p className="mt-2 text-gray-400 text-sm text-center">
            Continuing analysis for
            <br />
            accurate measurements...
          </p>
          <div className="mt-6 w-32 h-1 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full w-full bg-gradient-to-r from-[#1845a3] to-[#ED1C24] animate-pulse rounded-full" />
          </div>
        </div>
      )}

      {error.length > 0 && (
        <div className="fixed inset-0 px-8 flex flex-col items-center justify-center bg-white">
          <div className="relative">
            <div className="absolute -inset-4 rounded-full bg-red-100 animate-pulse" />
            <img className="relative shrink-0 w-20" src={Failure} alt="failure" />
          </div>
          <p className="mt-6 text-red-500 text-xl font-bold">Scan Failed!</p>
          <p className="mt-3 max-h-40 overflow-auto whitespace-pre-line text-gray-500 text-sm text-center leading-relaxed">{error}</p>
          <button
            className="mt-8 px-8 py-3 rounded-xl bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white text-sm font-semibold shadow-lg"
            type="button"
            onClick={() => navigate(-1)}
          >
            Try Again
          </button>
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-white">
          <LoadingScreen style={{ minHeight: "auto" }} />
          <p className="mt-2 text-xs text-gray-400">Preparing camera...</p>
        </div>
      )}
    </div>
  );
};

export default FingerScan;
